import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PLATFORM_ROLE_HINTS,
  PLATFORM_ROLE_LABELS,
  PLATFORM_ROLES,
  type PlatformRole,
  type TeamMemberCreated,
  type TeamMemberDTO,
} from '@bifeai/shared';
import { Badge, Button, Callout, Card, CardBody, Dialog, EmptyState, Field, Input, PageHeader, Select, Skeleton, useToast } from '@bifeai/ui';
import { KeyRound, Plus, UserCheck, UserX, Users } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { useMeQuery } from '../lib/session';

/** Equipo del panel admin: quién entra y con qué rol. Solo el Administrador. */
export function TeamPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const me = useMeQuery();
  const [adding, setAdding] = useState(false);
  const [secret, setSecret] = useState<{ title: string; email: string; password: string } | null>(null);
  const list = useQuery({ queryKey: ['team'], queryFn: () => api.get<TeamMemberDTO[]>('/platform/team') });
  const refresh = () => qc.invalidateQueries({ queryKey: ['team'] });

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { role?: PlatformRole; isActive?: boolean } }) => api.patch(`/platform/team/${id}`, body),
    onSuccess: async () => {
      toast.success('Listo', 'Se cerraron sus sesiones abiertas.');
      await refresh();
    },
    onError: (err) => toast.error('No se pudo cambiar', errorMessage(err)),
  });
  const reset = useMutation({
    mutationFn: (m: TeamMemberDTO) =>
      api.post<{ temporaryPassword: string }>(`/platform/team/${m.id}/reset-password`).then((r) => ({ m, password: r.temporaryPassword })),
    onSuccess: ({ m, password }) => setSecret({ title: `Nueva contraseña temporal de ${m.name}`, email: m.email, password }),
    onError: (err) => toast.error('No se pudo resetear', errorMessage(err)),
  });

  return (
    <>
      <PageHeader
        title="Equipo"
        subtitle="Las personas que entran a este panel y qué puede hacer cada una."
        actions={
          <Button variant="primary" icon={<Plus />} onClick={() => setAdding(true)}>
            Sumar persona
          </Button>
        }
      />
      <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', marginBottom: 18 }}>
        {PLATFORM_ROLES.map((r) => (
          <div key={r} className="bf-cell-sub" style={{ border: '1px solid var(--bf-line)', borderRadius: 12, padding: '10px 12px' }}>
            <strong style={{ color: 'var(--bf-ink)' }}>{PLATFORM_ROLE_LABELS[r]}</strong>
            <div>{PLATFORM_ROLE_HINTS[r]}</div>
          </div>
        ))}
      </div>
      <Card>
        <CardBody flush>
          {list.isPending ? (
            <Skeleton style={{ height: 160 }} />
          ) : !list.data?.length ? (
            <EmptyState icon={<Users />} title="Sin personas" />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Persona</th>
                    <th>Rol</th>
                    <th>Estado</th>
                    <th>Último ingreso</th>
                    <th aria-label="Acciones" />
                  </tr>
                </thead>
                <tbody>
                  {list.data.map((m) => {
                    const self = m.id === me.data?.admin.id;
                    return (
                      <tr key={m.id}>
                        <td>
                          <span className="bf-cell-main">
                            {m.name}
                            {self ? ' (vos)' : ''}
                          </span>
                          <div className="bf-cell-sub">{m.email}</div>
                        </td>
                        <td>
                          <Select
                            aria-label={`Rol de ${m.name}`}
                            value={m.role}
                            disabled={self || update.isPending}
                            onChange={(e) => update.mutate({ id: m.id, body: { role: e.target.value as PlatformRole } })}
                            style={{ minWidth: 150 }}
                          >
                            {PLATFORM_ROLES.map((r) => (
                              <option key={r} value={r}>
                                {PLATFORM_ROLE_LABELS[r]}
                              </option>
                            ))}
                          </Select>
                        </td>
                        <td>
                          {!m.isActive ? (
                            <Badge dot>Desactivada</Badge>
                          ) : !m.totpEnabled || m.mustChangePassword ? (
                            <Badge tone="warning" dot>
                              Falta su primer ingreso
                            </Badge>
                          ) : (
                            <Badge tone="success" dot>
                              Activa
                            </Badge>
                          )}
                        </td>
                        <td className="bf-cell-sub">{m.lastLoginAt ? formatDateTime(m.lastLoginAt) : 'Nunca'}</td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {!self && (
                            <>
                              <Button size="sm" variant="ghost" icon={<KeyRound />} onClick={() => reset.mutate(m)}>
                                Nueva contraseña
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                icon={m.isActive ? <UserX /> : <UserCheck />}
                                onClick={() => update.mutate({ id: m.id, body: { isActive: !m.isActive } })}
                              >
                                {m.isActive ? 'Desactivar' : 'Activar'}
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      {adding && (
        <AddDialog
          onClose={() => setAdding(false)}
          onDone={async (r) => {
            setAdding(false);
            setSecret({ title: `${r.member.name} ya puede entrar`, email: r.member.email, password: r.temporaryPassword });
            await refresh();
          }}
        />
      )}
      {secret && (
        <Dialog open onOpenChange={(o) => !o && setSecret(null)} title={secret.title}>
          <div className="bf-form">
            <Callout>
              Le mandamos un email con el acceso. Por las dudas, acá está la contraseña temporal: se ve una sola vez. Al entrar va a activar la
              verificación en dos pasos y cambiarla.
            </Callout>
            <Field label="Usuario">
              <Input readOnly value={secret.email} />
            </Field>
            <Field label="Contraseña temporal">
              <Input readOnly value={secret.password} style={{ fontFamily: 'monospace' }} onFocus={(e) => e.target.select()} />
            </Field>
            <div className="bf-form-actions">
              <Button variant="primary" onClick={() => setSecret(null)}>
                Listo
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}

function AddDialog({ onClose, onDone }: { onClose: () => void; onDone: (r: TeamMemberCreated) => Promise<void> }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<PlatformRole>('VIEWER');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setError(null);
    setBusy(true);
    try {
      await onDone(await api.post<TeamMemberCreated>('/platform/team', { name, email, role }));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Sumar persona al panel">
      <div className="bf-form">
        {error && <Callout tone="danger">{error}</Callout>}
        <Field label="Nombre">
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <Field label="Email">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Rol" hint={PLATFORM_ROLE_HINTS[role]}>
          <Select value={role} onChange={(e) => setRole(e.target.value as PlatformRole)}>
            {PLATFORM_ROLES.map((r) => (
              <option key={r} value={r}>
                {PLATFORM_ROLE_LABELS[r]}
              </option>
            ))}
          </Select>
        </Field>
        <div className="bf-form-actions">
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" loading={busy} disabled={name.trim().length < 2 || !email.includes('@')} onClick={() => void save()}>
            Sumar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
